import * as React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { useState, useEffect } from 'react';
import { FormControlLabel, RadioGroup, Radio } from '@mui/material';
import Chip from '@mui/material/Chip';
import { ComponentSpinner } from '../../../../Compounds/Loading/ComponentSpinner';
import { TextBlock } from '../../../../Atoms/Text';
import { SearchBox } from '../../../../Compounds/Search/Search';
import { EntityType, SearchResult } from '../../../../../store/Search';
import { createPaymentFrequencyException } from '../../../../../Api/PaymentFrequencyException/PaymentFrequencyExceptionApi';
import { chipStyles } from '../../../../../utils/helpers';
import { PaymentFrequency } from '../../../../../utils/constants';
import { useSnackbar } from '../../../../../Context/SnackbarContext';

interface NewRecordDialogProps {
  open: boolean;
  onClose: (refresh?: boolean) => void;
}

interface OptionCustomer {
  id: string;
  name: string;
}

const radioStyle = { height: '2rem', width: '2rem', m: '0 0.3rem' };

export const NewRecordDialog = ({ open, onClose }: NewRecordDialogProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setSnack } = useSnackbar();
  const [paymentFrequency, setPaymentFrequency] = useState<PaymentFrequency>(PaymentFrequency.QUARTERLY);

  const [supplier, setSupplier] = useState<OptionCustomer | null>(null);

  useEffect(() => {
    if (open) {
      setPaymentFrequency(PaymentFrequency.QUARTERLY);
      setSupplier(null);
    }
  }, [open]);

  const handleSelect = (result: SearchResult) => {
    const { id, title } = result;
    setSupplier({ id: id, name: title });
  };

  const handleSave = async () => {
    if (!supplier) return;
    setIsSubmitting(true);
    const { data, error } = await createPaymentFrequencyException({
      supplierId: supplier.id,
      paymentFrequency
    });
    if (data) {
      onClose(true);
    }
    if (error) {
      setSnack({ message: error.message, type: 'error', open: true });
    }
    setIsSubmitting(false);
  };

  const handleClose = () => {
    onClose();
  };

  const getPaymentFrequencyLabel = (value: PaymentFrequency) => {
    return (
      <Chip
        label={value}
        size="small"
        sx={chipStyles(value)}
        title={value}
      />
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: (event: React.SubmitEvent) => {
            event.preventDefault();
            handleSave();
          },
          sx: { overflowY: 'visible' }
        }
      }}
      fullWidth
      maxWidth="sm"
      data-testid="edit-relationship-modal">
      <DialogTitle>
        <TextBlock
          weight="bold"
          size="lg"
          color="grey9">
          New Payment Frequency Exception
        </TextBlock>
      </DialogTitle>
      <DialogContent sx={{ minHeight: 70, overflowY: 'visible', paddingLeft: 3, paddingRight: 3 }}>
        {isSubmitting && <ComponentSpinner label="Saving..." />}
        {!isSubmitting && (
          <div className="flex flex-row gap-x-10 w-full">
            <div className="flex flex-col gap-y-3 w-1/2">
              <TextBlock color="grey6">Supplier</TextBlock>
              <SearchBox
                handleSelect={handleSelect}
                searchEntityType={EntityType.SUPPLIER}
                inputString=""
                fullWidth
                placeholder=""
                handleClearSelect={() => setSupplier(null)}
              />
            </div>
            <div className="flex flex-col gap-y-3 w-1/2">
              <TextBlock color="grey6">Payment Frequency</TextBlock>
              <RadioGroup
                aria-label="assetField"
                name="assetField"
                value={paymentFrequency}
                onChange={(e) => {
                  const val = e.target.value as PaymentFrequency;
                  setPaymentFrequency(val);
                }}
                className="!m-0 p-0">
                <FormControlLabel
                  value={PaymentFrequency.QUARTERLY}
                  control={<Radio sx={radioStyle} />}
                  label={getPaymentFrequencyLabel(PaymentFrequency.QUARTERLY)}
                />
                <FormControlLabel
                  value={PaymentFrequency.ANNUALLY}
                  control={<Radio sx={radioStyle} />}
                  label={getPaymentFrequencyLabel(PaymentFrequency.ANNUALLY)}
                />
                <FormControlLabel
                  value={PaymentFrequency.ONE_TIME}
                  control={<Radio sx={radioStyle} />}
                  label={getPaymentFrequencyLabel(PaymentFrequency.ONE_TIME)}
                />
              </RadioGroup>
            </div>
          </div>
        )}
      </DialogContent>
      <hr className="mb-1 mx-6" />
      {!isSubmitting && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            data-testid="cancel-button"
            onClick={handleClose}>
            Cancel
          </Button>
          <Button
            disabled={!supplier}
            type="submit"
            variant="contained">
            Save
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
